import { useEffect, useMemo, useState } from "react";
import { requestServiceHelper } from "../../api/portalAssistant";
import { formatApiError } from "../../utils/formatApiError";

const DEFAULT_SUGGESTIONS = [
  "I want longer nails",
  "I want gel polish",
  "I need a refill",
  "I want nail art",
  "I want a brazilian wax",
  "I want lash extensions",
  "I want a facial",
  "I'm not sure",
];

const buildIntroMessage = () => ({
  id: "intro",
  role: "assistant",
  text:
    "Tell me what you want (example: longer nails, brazilian wax, lash extensions, facial, pedicure with callus care).",
});

const INTENT_LABELS = {
  NAILS: "Nails",
  WAXING: "Waxing",
  LASHES: "Lashes",
  FACIALS_ESTHETICS: "Facials & Esthetics",
  PMU: "PMU",
  OTHER: "Other",
  NAILS_NATURAL_HANDS: "Natural Nails",
  NAILS_ARTIFICIAL_HANDS: "Extensions",
  NAILS_FEET: "Pedicure",
  NAILS_ADD_ONS: "Nail Add-ons",
  WAX_FACE: "Face Waxing",
  WAX_BODY: "Body Waxing",
  WAX_BIKINI_BRAZILIAN: "Bikini/Brazilian Waxing",
  LASH_EXTENSIONS: "Lash Extensions",
  LASH_LIFT_TINT: "Lash Lift/Tint",
  FACIAL_BASIC: "Facial",
  FACIAL_ADVANCED: "Advanced Facial",
  ESTHETICS_BODY: "Body Treatments",
  PMU_BROWS_MICROBLADING: "PMU Brows",
  PMU_TOUCHUP: "PMU Touch-up",
  UNKNOWN: "General",
};

const formatCurrency = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "Pricing unavailable";
  return amount.toLocaleString("en-CA", {
    style: "currency",
    currency: "CAD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatPriceLine = (service) => {
  if (service?.price_label) return service.price_label;
  if (Number.isFinite(Number(service?.base_price))) {
    return formatCurrency(service.base_price);
  }
  return "Pricing unavailable";
};

export default function PortalServiceHelperModal({
  open,
  onClose,
  salonId,
  token,
  settings,
  assistantHandlers,
}) {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const portalAiEnabled = Boolean(settings?.portal_ai_enabled);
  const canUseAssistant = portalAiEnabled && Boolean(token);
  const autoSelectEnabled =
    settings?.portal_ai_autoselect_enabled !== false &&
    typeof assistantHandlers?.selectService === "function";
  const canViewService = typeof assistantHandlers?.viewService === "function";

  const introMessage = useMemo(() => buildIntroMessage(), []);

  useEffect(() => {
    if (!open) {
      setMessages([]);
      setInputValue("");
      setError("");
      setLoading(false);
      return;
    }
    setMessages([introMessage]);
  }, [open, introMessage]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleSend = async (text) => {
    const message = (text || inputValue || "").trim();
    if (!message || loading || !canUseAssistant) return;
    setError("");
    setInputValue("");
    setMessages((prev) => [
      ...prev,
      { id: `${Date.now()}-user`, role: "user", text: message },
    ]);
    setLoading(true);
    try {
      const contextPayload =
        typeof assistantHandlers?.getContext === "function"
          ? assistantHandlers.getContext()
          : { selected_category_id: "all", selected_service_id: null };
      const payload = {
        salon_id: Number(salonId),
        message,
        context: contextPayload,
      };
      const data = await requestServiceHelper(payload, token);
      setMessages((prev) => [
        ...prev,
        {
          id: `${Date.now()}-assistant`,
          role: "assistant",
          text: data?.assistant_text || "Here are a few options to consider.",
          recommendations: Array.isArray(data?.recommended_services)
            ? data.recommended_services
            : [],
          followUps: Array.isArray(data?.follow_up_questions)
            ? data.follow_up_questions
            : [],
          safetyNote: data?.safety_note || "",
          intent: data?.intent || "",
          intentConfidence:
            typeof data?.intent_confidence === "number"
              ? data.intent_confidence
              : null,
        },
      ]);
    } catch (err) {
      setError(formatApiError(err));
      setMessages((prev) => [
        ...prev,
        {
          id: `${Date.now()}-assistant-error`,
          role: "assistant",
          text:
            "Sorry, I couldn't reach the service helper. Please try again or select a service below.",
          recommendations: [],
          followUps: [],
          safetyNote: "",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = (value) => {
    if (!canUseAssistant) return;
    handleSend(value);
  };

  const handleSelectService = (serviceId, serviceName) => {
    if (!autoSelectEnabled) return;
    assistantHandlers?.selectService?.(serviceId, serviceName);
    onClose?.();
  };

  const handleViewService = (serviceId) => {
    if (!canViewService) return;
    assistantHandlers?.viewService?.(serviceId);
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4 py-6">
      <div className="w-full max-w-3xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between bg-rose-500 px-6 py-4">
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-white">
              Service Helper
            </h2>
            <p className="text-xs md:text-sm text-rose-100">
              Answer a few questions and get the best match.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full bg-white/15 px-3 py-1 text-sm font-semibold text-white hover:bg-white/25"
            aria-label="Close"
          >
            X
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-6 py-5 space-y-4">
          {!portalAiEnabled && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              This helper is not enabled for this salon yet.
            </div>
          )}
          {portalAiEnabled && !token && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Please sign in to use the service helper.
            </div>
          )}
          {messages.map((message) => (
            <div
              key={message.id}
              className={`rounded-2xl px-4 py-3 text-sm md:text-base ${
                message.role === "user"
                  ? "ml-auto max-w-[80%] bg-rose-50 text-rose-700 border border-rose-100"
                  : "mr-auto max-w-[85%] bg-slate-50 text-slate-700 border border-slate-200"
              }`}
            >
              <div className="whitespace-pre-wrap">{message.text}</div>

              {message.intent && (
                <div className="mt-2 text-[11px] uppercase tracking-wide text-slate-400">
                  Intent: {INTENT_LABELS[message.intent] || message.intent}
                  {typeof message.intentConfidence === "number" &&
                    message.intentConfidence > 0 && (
                    <span>
                      {" "}
                      · {Math.round(message.intentConfidence * 100)}%
                    </span>
                  )}
                </div>
              )}

              {message.intent === "NAILS_ARTIFICIAL_HANDS" && (
                <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                  For longer nails, you usually need extensions / artificial nails.
                </div>
              )}

              {message.recommendations?.length > 0 && (
                <div className="mt-3 grid grid-cols-1 gap-3">
                  {message.recommendations.map((service) => (
                    <div
                      key={service.service_id}
                      className="rounded-2xl border border-slate-200 bg-white p-4"
                    >
                      <div className="text-sm md:text-base font-semibold text-slate-900">
                        {service.service_name}
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        Duration: {service.duration_minutes || "?"} mins
                        {" · "}
                        Price: {formatPriceLine(service)}
                      </div>
                      {service.price_note && (
                        <div className="mt-2 text-xs text-slate-500">
                          {service.price_note}
                        </div>
                      )}
                      {service.reason && (
                        <div className="mt-2 text-xs text-slate-600">
                          Why: {service.reason}
                        </div>
                      )}
                      <div className="mt-3 flex flex-wrap gap-2">
                        {autoSelectEnabled && (
                          <button
                            type="button"
                            onClick={() =>
                              handleSelectService(
                                service.service_id,
                                service.service_name
                              )
                            }
                            className="rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-800"
                          >
                            Select this service
                          </button>
                        )}
                        {canViewService && (
                          <button
                            type="button"
                            onClick={() => handleViewService(service.service_id)}
                            className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          >
                            View details
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {message.followUps?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {message.followUps.map((question) => (
                    <button
                      key={question}
                      type="button"
                      onClick={() => handleSuggestionClick(question)}
                      className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              )}

              {message.safetyNote && (
                <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  {message.safetyNote}
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
              Thinking...
            </div>
          )}

          {error && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {error}
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 bg-white px-6 py-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            {DEFAULT_SUGGESTIONS.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                onClick={() => handleSuggestionClick(suggestion)}
                disabled={!canUseAssistant}
                className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100"
              >
                {suggestion}
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <input
              type="text"
              value={inputValue}
              onChange={(event) => setInputValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Describe what you want..."
              disabled={!canUseAssistant}
              className="h-11 flex-1 rounded-xl border border-slate-300 px-4 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
            />
            <button
              type="button"
              onClick={() => handleSend()}
              disabled={!inputValue.trim() || loading || !canUseAssistant}
              className="rounded-full bg-slate-900 px-5 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {loading ? "Sending..." : "Send"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
